import React, { useContext, useMemo } from 'react';
import { Button, Form, FormInstance, Select, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import ActionTypes from '@utils/deviceActions';
import {
  DeviceActionProps,
  DeviceActionStepsProps,
} from '@components/Actions/ActionsModal/types';
import { IDevice } from '@pages/Assets/Inventory/types';
import rolesConstants, { RolesTypes } from '@utils/rolesConstants';
import useSwr from '@hooks/useSwr';
import { InfoCircleOutlined } from '@ant-design/icons';
import SessionContext from '@providers/Session/SessionContext';

interface OperatorRestrictionPayloadProps {
  action: ActionTypes.OPERATOR_RESTRICTION;
  operatorRestriction: string;
  devices: {
    msisdn: string;
    iccid: string;
  }[];
}

interface OperatorRestrictionProps {
  devices: IDevice[];
  isUpload?: boolean;
  form: FormInstance<any>;
  actionRole: RolesTypes | undefined;
}

const useOperatorRestriction = ({
  devices,
  isUpload,
  form,
  actionRole,
}: OperatorRestrictionProps): DeviceActionProps => {
  const { t } = useTranslation('actions-modal');
  const { isMot } = useContext(SessionContext);

  const selectedRestriction = Form.useWatch('selectedRestriction', form);

  const { data: dataRestriction } = useSwr<string[]>(
    actionRole === rolesConstants.OPERATOR_RESTRICTION
      ? '/service-proxy/hlr-operators'
      : null,
  );

  const restrictionOptions = useMemo(
    () =>
      dataRestriction
        ?.filter(operator => !operator.includes('TODAS OPERADORAS'))
        ?.map(operator => ({
          label: operator,
          value: operator,
        })),
    [dataRestriction],
  );

  const steps = useMemo(
    (): DeviceActionStepsProps[] => [
      {
        title: (
          <>
            {t('operator-restriction.allowedOperators')}

            <Tooltip
              title={t('operator-restriction.operatorRestrictionTooltip')}
            >
              <Button type="link" size="small" icon={<InfoCircleOutlined />} />
            </Tooltip>
          </>
        ),
        name: 'selectedRestriction',
        content: <Select options={restrictionOptions} />,
        showWhenUpload: true,
      },
    ],
    [restrictionOptions, t],
  );

  const payloadToSend = useMemo(
    (): OperatorRestrictionPayloadProps => ({
      action: ActionTypes.OPERATOR_RESTRICTION,
      operatorRestriction: selectedRestriction,
      devices: isUpload
        ? devices
        : devices?.map(device => ({
            msisdn: device.msisdn,
            iccid: device.iccid,
          })),
    }),
    [devices, selectedRestriction, isUpload],
  );

  return {
    steps,
    title: t(
      `device-actions:labels.${isMot ? 'mot' : 'common'}.${
        rolesConstants.OPERATOR_RESTRICTION
      }`,
    ),
    payloadToSend,
    uploadPosition: 1,
    infoMessage: isMot && (
      <p style={{ margin: 0, fontWeight: 'bold' }}>
        {t('just-algar-lines')?.toUpperCase()}
      </p>
    ),
  };
};

export default useOperatorRestriction;
